import React, { useEffect, useState } from 'react';
import { dpsTheme, getScoreLevelConfig } from '../../../styles/dpsTheme';

interface ScoreCircleProps {
  score: number;
}

export const ScoreCircle: React.FC<ScoreCircleProps> = ({ score }) => {
  const [animatedScore, setAnimatedScore] = useState(0);
  const levelConfig = getScoreLevelConfig(score);
  const circumference = 2 * Math.PI * 85;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  useEffect(() => {
    const duration = 1500; // Durée totale fixe
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const current = Math.floor(progress * score);
      
      setAnimatedScore(current);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setAnimatedScore(score);
      }
    };
    
    requestAnimationFrame(animate);
  }, [score]);

  return (
    <div
      className="bg-gradient-to-br backdrop-blur-xl border rounded-lg p-6 sm:p-8 lg:p-12 flex flex-col items-center text-center h-full"
      style={{
        background: `linear-gradient(135deg, ${levelConfig.bg}, rgba(15, 23, 42, 0.4))`,
        borderColor: levelConfig.border,
      }}
    >
      <div
        className="px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold uppercase tracking-wide mb-4 sm:mb-6"
        style={{
          background: levelConfig.bg,
          borderColor: levelConfig.border,
          color: levelConfig.text,
          border: `1px solid ${levelConfig.border}`,
        }}
      >
        {levelConfig.label}
      </div>

      <div className="relative w-32 h-32 sm:w-40 sm:h-40 lg:w-44 lg:h-44 mb-3 sm:mb-4">
        <svg className="w-full h-full" style={{ transform: 'rotate(-90deg)' }}>
          <defs>
            <linearGradient id={`scoreGrad-${score}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: levelConfig.primary }} />
              <stop offset="100%" style={{ stopColor: levelConfig.secondary }} />
            </linearGradient>
          </defs>
          <circle
            cx="50%"
            cy="50%"
            r="42%"
            fill="none"
            stroke="rgba(255,255,255,0.05)"
            strokeWidth="8"
          />
          <circle
            cx="50%"
            cy="50%"
            r="42%"
            fill="none"
            stroke={`url(#scoreGrad-${score})`}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            style={{ transition: 'stroke-dashoffset 1.5s ease-in-out' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white">
            {animatedScore}
            <span className="text-lg sm:text-xl text-gray-600">/100</span>
          </div>
        </div>
      </div>

      <div className="text-sm sm:text-base text-gray-400 mt-auto">
        Score de sécurité global
      </div>
    </div>
  );
};
